/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { CoreErrorKey } from '../key/core-error.key';
import { CoreValidationKey } from '../key/core-validation.key';
import { ApplicationError } from '../model/application-error.model';
import { ValidationError } from '../model/validation-error.model';
import { ValidationPredicate } from '../model/validation-predicate.model';
import { ValidationRules } from '../model/validation-rules.model';
import { ValidationProvider } from '../provider/validation.provider';
import { ObjectAdapter } from '../util/object-adapter.util';
import { toPromise } from './to-promise.helper';

export async function validate(
  rules: ValidationRules, value: any, key?: string, scope?: string, context?: object
): Promise<ValidationError[]> {
  try {
    if (rules === undefined || rules === null) {
      throw new ApplicationError({
        key: CoreErrorKey.VALIDATION_RULES_MISSING,
        message: 'Validation rules missing'
      });
    }
    if (typeof rules === 'boolean') {
      if (!rules) {
        // do not validate
        return null;
      }
      // default validation (required)
      const validationError = await evaluateValidationRule(
        CoreValidationKey.REQUIRED, undefined, value, key, scope, context
      );
      return validationError ? [validationError] : null;
    }
    if (typeof rules === 'string') {
      // validation key
      const validationError = await evaluateValidationRule(
        rules, undefined, value, key, scope, context
      );
      return validationError ? [validationError] : null;
    }
    if (Array.isArray(rules)) {
      // array of validation rules
      return await rules.reduce(async (resultPromise: Promise<ValidationError[]>, rule) => {
        const result: ValidationError[] = await resultPromise;
        const ruleValidationErrors = await validate(rule, value, key, scope, context);
        if (!ruleValidationErrors) {
          return result;
        }
        if (!result) {
          return ruleValidationErrors;
        }
        result.push(...ruleValidationErrors);
        return result;
      }, Promise.resolve(null)) as ValidationError[];
    }
    if (typeof rules === 'object') {
      // object of validation keys and validation options
      const validationKeys: string[] = Object.keys(rules);
      const validationErrors = (await Promise.all(validationKeys
        .map((validationKey: string) => evaluateValidationRule(
          validationKey, rules[validationKey], value, key, scope, context
        ))
      )).filter((validationError) => !!validationError);
      return validationErrors.length > 0 ? validationErrors : null;
    }
    throw new ApplicationError({
      key: CoreErrorKey.VALIDATION_RULES_INVALID,
      arguments: { rules },
      message: 'Validation rules invalid'
    });
  } catch (error) {
    throw new ApplicationError({
      key: CoreErrorKey.VALIDATION_ERROR,
      arguments: { error },
      message: `Validation error: ${error.message}`
    });
  }
}

async function evaluateValidationRule(
  validationKey: string, options: any, value: any, key: string, scope: string, context: object
): Promise<ValidationError> {
  if (!validationKey) {
    throw new ApplicationError({
      key: CoreErrorKey.VALIDATION_KEY_MISSING,
      message: 'Validation key missing'
    });
  }
  let validationPredicate: ValidationPredicate;
  if (typeof options === 'function') {
    validationPredicate = options;
    options = undefined;
  } else {
    validationPredicate = ValidationProvider.resolvePredicate(validationKey);
    if (!validationPredicate) {
      throw new ApplicationError({
        key: CoreErrorKey.VALIDATION_KEY_INVALID,
        arguments: { validationKey },
        message: 'Validation key invalid'
      });
    }
  }
  return toPromise(() => validationPredicate(value, options, context, key, scope))
    .then((result: boolean) => {
      return result === true ? null : new ValidationError({
        validationKey,
        validationArguments: { value, options, context, key, scope }
      });
    })
    .catch((error: ApplicationError) => {
      throw new ApplicationError({
        key: CoreErrorKey.VALIDATION_KEY_EVALUATION,
        arguments: {
          validationKey,
          validationArguments: { value, options, context, key, scope },
          error
        },
        message: `${error.message} (${validationKey})`
      });
    });

}

export function getValidationContextValue(context: object, key: string): any {
  return toValidationContextAdapter(context).get(key);
}

export function toValidationContextAdapter(context: object): ObjectAdapter {
  if (!context) {
    throw new ApplicationError({
      key: CoreErrorKey.VALIDATION_CONTEXT_MISSING,
      message: 'Validation context missing'
    });
  }
  try {
    return ObjectAdapter.from(context);
  } catch {
    throw new ApplicationError({
      key: CoreErrorKey.VALIDATION_CONTEXT_INVALID,
      arguments: { context },
      message: 'Validation context invalid'
    });
  }
}
